<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import axios from 'axios'
import { useHttpErrorsHandler } from '../Composables/httpErrorsHandler.js'

const props = defineProps({
    // 'news' ou 'comment'
    reactableType: { type: String, required: true },
    reactableId: { type: Number, required: true },
    reactions: { type: Array, default: () => [] },
    size: { type: String, default: 'sm' },
})

// Mêmes emojis que côté serveur (Reaction::EMOJIS)
const EMOJIS = ['👍', '❤️', '😂', '🎉', '👀', '🙏']

const { httpErrorsHandler } = useHttpErrorsHandler()

const items = ref([...props.reactions])
const pickerOpen = ref(false)
const saving = ref(false)
const root = ref(null)

watch(() => props.reactions, (value) => {
    items.value = [...(value ?? [])]
})

const who = (reaction) => reaction.users.join(', ')

// Une seule réaction par personne : celle qu'on a choisie est mise en avant dans le sélecteur
const myEmoji = computed(() => items.value.find((reaction) => reaction.reacted)?.emoji ?? null)

// Fermeture au clic en dehors ou avec Échap
function onDocumentClick(event) {
    if (root.value && !root.value.contains(event.target)) {
        pickerOpen.value = false
    }
}

function onKeydown(event) {
    if (event.key === 'Escape') {
        pickerOpen.value = false
    }
}

watch(pickerOpen, (open) => {
    if (open) {
        document.addEventListener('click', onDocumentClick)
        document.addEventListener('keydown', onKeydown)
    } else {
        document.removeEventListener('click', onDocumentClick)
        document.removeEventListener('keydown', onKeydown)
    }
})

onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick)
    document.removeEventListener('keydown', onKeydown)
})

function toggle(emoji) {
    if (saving.value) return

    pickerOpen.value = false
    saving.value = true

    axios.post('/api/reactions', { reactable_type: props.reactableType, reactable_id: props.reactableId, emoji })
        .then((response) => {
            items.value = response.data.data
        })
        .catch((error) => httpErrorsHandler(error))
        .finally(() => {
            saving.value = false
        })
}
</script>

<template>
    <div ref="root" class="reaction-bar" :class="`reaction-bar-${size}`">
        <transition-group name="reaction-chip" tag="div" class="reaction-chips">
            <button
                v-for="reaction in items"
                :key="reaction.emoji"
                type="button"
                class="reaction-chip"
                :class="{ 'is-mine': reaction.reacted }"
                v-tooltip="who(reaction)"
                @click="toggle(reaction.emoji)"
            >
                <span class="reaction-emoji">{{ reaction.emoji }}</span>
                <span class="reaction-count">{{ reaction.count }}</span>
            </button>
        </transition-group>

        <div class="reaction-trigger">
            <button type="button" class="reaction-add" :class="{ 'is-open': pickerOpen }" v-tooltip="'Réagir'" @click="pickerOpen = !pickerOpen">
                <i class="mdi mdi-emoticon-happy-outline"></i>
            </button>

            <!-- Par-dessus la ligne, à droite du bouton : rien ne bouge à l'ouverture -->
            <transition name="reaction-picker">
                <div v-if="pickerOpen" class="reaction-picker">
                    <button
                        v-for="(emoji, index) in EMOJIS"
                        :key="emoji"
                        type="button"
                        class="reaction-picker-item"
                        :class="{ 'is-selected': emoji === myEmoji }"
                        :style="{ transitionDelay: `${index * 25}ms` }"
                        @click="toggle(emoji)"
                    >{{ emoji }}</button>
                </div>
            </transition>
        </div>
    </div>
</template>

<style scoped>
.reaction-bar {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
}

.reaction-chips {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0.4rem;
}

.reaction-chip,
.reaction-add,
.reaction-picker-item {
    border: 0;
    background: none;
    padding: 0;
    cursor: pointer;
}

.reaction-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    height: 1.75rem;
    padding: 0 0.6rem;
    border-radius: 999px;
    border: 1px solid transparent;
    background-color: var(--vz-light);
    font-size: 0.75rem;
    color: var(--vz-secondary-color);
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.reaction-chip:hover {
    border-color: var(--vz-border-color);
}

.reaction-chip.is-mine {
    background-color: var(--vz-secondary-bg-subtle);
    border-color: var(--vz-secondary-border-subtle);
    color: var(--vz-secondary);
    font-weight: 600;
}

.reaction-emoji {
    font-size: 1rem;
    line-height: 1;
}

.reaction-trigger {
    position: relative;
    display: inline-flex;
}

.reaction-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    font-size: 1.1rem;
    color: var(--vz-secondary-color);
    transition: background-color 0.15s ease, color 0.15s ease;
}

.reaction-add:hover,
.reaction-add.is-open {
    background-color: var(--vz-light);
    color: var(--vz-body-color);
}

.reaction-picker {
    position: absolute;
    top: 50%;
    left: calc(100% + 0.35rem);
    z-index: 5;
    display: flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.2rem 0.35rem;
    border: 1px solid var(--vz-border-color);
    border-radius: 999px;
    background-color: var(--vz-secondary-bg);
    white-space: nowrap;
    transform: translateY(-50%);
    transform-origin: left center;
}

.reaction-picker-item {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.8rem;
    height: 1.8rem;
    border-radius: 50%;
    font-size: 1.1rem;
    line-height: 1;
    transition: transform 0.15s ease, background-color 0.15s ease, opacity 0.2s ease;
}

.reaction-picker-item:hover {
    background-color: var(--vz-light);
    transform: scale(1.2);
}

.reaction-picker-item.is-selected {
    background-color: var(--vz-secondary-bg-subtle);
}

.reaction-bar-md .reaction-chip {
    height: 2.1rem;
    padding: 0 0.8rem;
    font-size: 0.875rem;
}

.reaction-bar-md .reaction-emoji {
    font-size: 1.2rem;
}

.reaction-bar-md .reaction-add {
    width: 2.1rem;
    height: 2.1rem;
    font-size: 1.3rem;
}

/* Ouverture : le sélecteur se déplie depuis le bouton, les emojis apparaissent un par un */
.reaction-picker-enter-active,
.reaction-picker-leave-active {
    transition: opacity 0.18s ease, transform 0.18s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}

.reaction-picker-enter-from,
.reaction-picker-leave-to {
    opacity: 0;
    transform: translateY(-50%) scale(0.6);
}

.reaction-picker-enter-from .reaction-picker-item {
    opacity: 0;
    transform: translateX(-0.4rem);
}

.reaction-picker-leave-active {
    transition-duration: 0.12s;
}

/* Ajout ou retrait d'une réaction : fondu et léger zoom, sans à-coup */
.reaction-chip-enter-active,
.reaction-chip-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
}

.reaction-chip-enter-from,
.reaction-chip-leave-to {
    opacity: 0;
    transform: scale(0.7);
}

.reaction-chip-leave-active {
    position: absolute;
}

.reaction-chip-move {
    transition: transform 0.18s ease;
}
</style>
